import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { pagosApi } from '../api/pagos'
import EstadoBadgePago from '../components/EstadoBadgePago'
import MoneyInput from '../components/MoneyInput'
import { fmtFechaHora, fmtFecha, fmtMonto, hoyISO } from '../utils/fecha'
import { etiquetaCliente } from '../utils/cliente'
import Boton from '../components/Boton'
import ModalImputarRecibo from '../components/ModalImputarRecibo'
import ModalCambiarClienteRecibo from '../components/ModalCambiarClienteRecibo'
import AvisosCard from '../components/avisos/AvisosCard'
import { descargarArchivo } from '../utils/descargar'

// "Manual no enviado a Presea: libre" — confirmado sin número fiscal sigue
// siendo un papel de LGR.
const PUEDE_ANULAR = ['pendiente', 'confirmado', 'error']
const PUEDE_IMPUTAR = ['pendiente', 'confirmado', 'acreditado']

// Etiquetas de pago_retenciones.tipo (mismo catálogo que el impreso del recibo)
const RETENCION_LABEL = {
  iibb: 'Retención Ing. Brutos (IIBB)', iva: 'Retención IVA',
  ganancias: 'Retención Ganancias', suss: 'Retención SUSS', sellos: 'Retención Sellos',
}

export default function PagoDetallePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [pago, setPago] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accion, setAccion] = useState('')  // 'confirmar' | 'enviar' | 'anular' | 'rechazar' | 'devolver' | 'eliminar' — para deshabilitar
  const [error, setError] = useState('')
  const [showImputar, setShowImputar] = useState(false)
  const [showCliente, setShowCliente] = useState(false)
  const [showAnular, setShowAnular] = useState(false)
  const [showDevolucion, setShowDevolucion] = useState(false)

  const cargar = async () => {
    setLoading(true)
    try {
      const { data } = await pagosApi.obtener(id)
      setPago(data)
    } catch { navigate('/pagos') } finally { setLoading(false) }
  }

  useEffect(() => { cargar() }, [id])

  const correrAccion = async (clave, prompt, apiCall, postNavigate = false) => {
    if (prompt && !window.confirm(prompt)) return
    setAccion(clave); setError('')
    try {
      const { data } = await apiCall()
      if (postNavigate) navigate(-1)
      else if (data && data.id) setPago(data)
      else cargar()
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors?.estado?.[0] || 'Error en la acción.')
    } finally { setAccion('') }
  }

  if (loading) return <p style={{ color: '#64748b' }}>Cargando pago...</p>
  if (!pago) return null

  // Cobro ONLINE = vino por una pasarela (Magento, tienda propia, suscripción
  // Mobbex): esos no se "anulan" en LGR — si se cayeron/anularon en la
  // pasarela sin acreditarse, se RECHAZAN.
  const esOnline = ['magento', 'storefront', 'mobbex'].includes(pago.origen)
  // Operatoria de recibos (Martin, 16 ago 2026): ANULAR dentro del día
  // (verificado contra Presea y la pasarela) · DEVOLUCIÓN con contra-recibo
  // negativo total o parcial (pasado el día). Eliminar y reemitir, afuera.
  const tieneRecibo = !!pago.numero_presea
  const reciboDeHoy = tieneRecibo && String(pago.fecha).slice(0, 10) === hoyISO()
  const esDevolucionDoc = !!pago.devolucion_de_id
  const nBcos = (pago.valores || []).map(v => Number(v.n_bco))
  const viaMobbex = nBcos.some(n => n >= 1400 && n <= 1408) || /mobbex|sugapay/i.test(pago.observaciones || '')
  const viaMp = nBcos.includes(2000) || /mercado ?pago/i.test(pago.observaciones || '')
  // Sin recibo fiscal el papel es de LGR: anular libre (Magento no: rechazar).
  const puedeAnular = !tieneRecibo && !esOnline && PUEDE_ANULAR.includes(pago.estado)
  // Cobro ONLINE que nunca se acreditó y se cayó/anuló en la pasarela
  // (caso pago #787, Martin 1 sep 2026): el estado del circuito es RECHAZADO.
  // El endpoint existía desde el diseño — el botón nunca se había cableado.
  const puedeRechazar = !tieneRecibo && esOnline && PUEDE_ANULAR.includes(pago.estado)
  // Con recibo fiscal: anulación SOLO del día, verificada (modal con pasos).
  const puedeAnularVerificado = tieneRecibo && reciboDeHoy && !esDevolucionDoc
    && !['anulado', 'devuelto'].includes(pago.estado)
  const netoRestante = pago.importe_neto != null ? Number(pago.importe_neto) : Number(pago.importe)
  const yaDevuelto = Math.max(0, (Number(pago.importe) || 0) - netoRestante)
  const puedeDevolucion = tieneRecibo && pago.estado === 'acreditado' && !esDevolucionDoc && netoRestante > 0.005
  const puedeImputar = PUEDE_IMPUTAR.includes(pago.estado)
  const imputado = Number(pago.imputado) || 0
  const libre = pago.libre != null ? Number(pago.libre) : (Number(pago.importe) || 0) - imputado
  // CTA "pendiente de imputar": solo si todavía queda importe sin aplicar. Un
  // recibo totalmente imputado no se sigue ofreciendo como pendiente (sugerencia
  // #11). La re-distribución sigue disponible desde la card de imputación.
  // Un contra-recibo de DEVOLUCIÓN tiene importe (y libre) NEGATIVO: también
  // se aplica a una venta, y con `libre > 0` el botón nunca aparecía
  // (Martin, 23 sep 2026 — recibo #774).
  const esDevolucionNeg = (Number(pago.importe) || 0) < 0
  const puedeImputarPendiente = puedeImputar && Math.abs(libre) > 0.005
  // Cambiar el cliente: solo un recibo A CUENTA (el imputado lleva el cliente
  // de su venta). Los frenos finos (Presea) los aplica el backend.
  const puedeCambiarCliente = puedeImputar && (pago.notas_venta || []).length === 0
  const idsTransaccion = [...new Set([pago.id_transaccion, ...(pago.valores || []).map(v => v.id_transaccion)].filter(Boolean))]

  return (
    <div style={st.wrap}>
      <div style={st.headerRow}>
        <div style={st.headerLeft}>
          <h2 style={st.title}>Pago #{pago.id}</h2>
          <EstadoBadgePago estado={pago.estado} fallido={!!pago.cobro_fallido_at} aDevolver={!!pago.a_devolver_at && pago.estado !== 'devuelto'} />
        </div>
        <div style={st.headerActions}>
          <Boton variante="secondary"
            onClick={() => descargarArchivo(`/pagos/${pago.id}/pdf`, `recibo_${pago.numero_presea || pago.id}.pdf`)}>⬇ PDF</Boton>
          {pago.estado === 'pendiente' && (
            <>
              <Boton
                variante="success"
                disabled={accion === 'confirmar'}
                title="Marcar el cobro como confirmado (plata verificada en banco/MP/Mobbex). Solo desde 'confirmado' se manda a Presea."
                onClick={() => correrAccion('confirmar',
                  '¿Confirmar este cobro? Marcalo solo si verificaste que la plata entró en banco / MP / Mobbex.',
                  () => pagosApi.confirmar(id))}
              >{accion === 'confirmar' ? 'Confirmando…' : '✓ Confirmar'}</Boton>
            </>
          )}
          {/* Si el recibo se puede corregir lo dice el backend (`editable`), que
              es el que aplica la regla: mientras no tenga número de Presea ni
              esté viajando, sigue siendo un papel de LGR. Antes esta pantalla
              tenía su propia copia ("solo pendiente") y quedó desfasada. */}
          {pago.editable && (
            <Boton variante="secondary" onClick={() => navigate(`/pagos/${pago.id}/editar`)}>Editar</Boton>
          )}
          {pago.estado === 'confirmado' && (
            <Boton
              variante="send"
              disabled={accion === 'enviar'}
              title="Encolar este cobro para enviarlo a Presea (usar si la NV vinculada ya fue procesada y el envío automático no lo agarró)."
              onClick={() => correrAccion('enviar',
                '¿Enviar este cobro a Presea ahora?',
                () => pagosApi.enviarPresea(id))}
            >{accion === 'enviar' ? 'Enviando…' : '→ Enviar a Presea'}</Boton>
          )}
          {puedeAnular && (
            <Boton
              variante="destructive"
              disabled={accion === 'anular'}
              onClick={() => correrAccion('anular',
                '¿Confirmar anulación del pago? Esta acción no se puede deshacer.',
                () => pagosApi.anular(id))}
            >{accion === 'anular' ? 'Anulando…' : 'Anular'}</Boton>
          )}
          {puedeRechazar && (
            <Boton
              variante="destructive"
              disabled={accion === 'rechazar'}
              title="El cobro online nunca se acreditó (anulado/caído en la pasarela): queda RECHAZADO en LGR y no viaja a Presea. Verificalo antes en Mobbex/MP."
              onClick={() => {
                const motivo = window.prompt('Motivo del rechazo (ej: anulado en Mobbex el 01/09):')
                if (motivo === null) return
                correrAccion('rechazar', null, () => pagosApi.rechazar(id, motivo || null))
              }}
            >{accion === 'rechazar' ? 'Rechazando…' : 'Rechazar'}</Boton>
          )}
          {puedeAnularVerificado && (
            <Boton
              variante="destructive"
              title="Anulación del día: LGR verifica que el recibo figure ANUL. en Presea (y que la operación esté anulada en la pasarela, si vino por una) antes de anular acá."
              onClick={() => setShowAnular(true)}
            >Anular</Boton>
          )}
          {puedeDevolucion && (
            <Boton
              variante="secondary"
              title="Devolución total o parcial: LGR emite el contra-recibo NEGATIVO en Presea por el WS."
              onClick={() => setShowDevolucion(true)}
            >Devolución</Boton>
          )}
          {/* "Eliminar" quedó AFUERA de la operatoria de recibos (Martin,
              16 ago): un papel mal cargado se ANULA (queda la huella), no se
              borra. */}
          {puedeImputarPendiente && (
            <Boton variante="secondary"
              title={esDevolucionNeg
                ? 'Aplicar esta devolución a la venta de la que se devolvió la plata: resta de lo cobrado.'
                : 'Aplicar este recibo a comprobantes del cliente (mismo selector que la cobranza desde NV).'}
              onClick={() => setShowImputar(true)}>{esDevolucionNeg ? '↪ Aplicar devolución' : '↪ Imputar a NV'}</Boton>
          )}
          <Boton variante="ghost" onClick={() => navigate(-1)}>← Volver</Boton>
        </div>
      </div>

      {error && <div style={st.errorBox}>{error}</div>}

      {/* Fraude con la plata ya cobrada (19 oct 2026): la devolución la hace
          una persona en la pasarela; acá se cierra. */}
      {pago.a_devolver_at && pago.estado !== 'devuelto' && (
        <div style={st.errorBox}>
          <strong>Hay que devolver esta plata.</strong> {pago.a_devolver_motivo}
          {pago.numero_presea
            ? ' Como ya tiene recibo en Presea, registrala con «Devolución».'
            : (
              <div style={{ marginTop: 8 }}>
                <Boton variante="primary" sm disabled={!!accion}
                  onClick={() => correrAccion('devolver', '¿Confirmás que la plata ya se devolvió en la pasarela? El cobro pasa a DEVUELTO.', () => pagosApi.devueltoEnPasarela(id))}>
                  {accion === 'devolver' ? 'Guardando…' : '✓ Ya la devolví'}
                </Boton>
              </div>
            )}
        </div>
      )}

      <div style={st.grid}>
        {/* Info principal */}
        <div style={st.card}>
          <h3 style={st.cardTitle}>Datos del pago</h3>
          <dl style={st.dl}>
            <dt style={st.dt}>Cliente</dt>
            <dd style={st.dd}>
              <strong>{etiquetaCliente(pago.cliente)}</strong>
              <span style={{ color: '#64748b', marginLeft: '0.5rem', fontSize: '0.8125rem' }}>{pago.cliente?.cuit}</span>
              {puedeCambiarCliente && (
                <Boton variante="secondary" sm style={{ marginLeft: '0.5rem' }}
                  title="Cambiar el cliente de este recibo a cuenta y después imputarlo a una venta del cliente nuevo"
                  onClick={() => setShowCliente(true)}>✏️ Cambiar cliente</Boton>
              )}
            </dd>

            <dt style={st.dt}>Fecha</dt>
            <dd style={st.dd}>{fmtFecha(pago.fecha)}</dd>

            <dt style={st.dt}>Importe</dt>
            <dd style={{ ...st.dd, fontWeight: 700, fontSize: '1.125rem', color: '#1e40af' }}>
              ${fmtMonto(pago.importe)}
            </dd>

            {/* Retenciones del recibo: si las hay, se especifican SIEMPRE
                (Martin, 7 sep 2026) — típicas de un recibo hecho en Presea. */}
            {(pago.retenciones || []).length > 0 && (
              <>
                <dt style={st.dt}>Retenciones</dt>
                <dd style={st.dd}>
                  {pago.retenciones.map(r => (
                    <div key={r.id}>
                      {RETENCION_LABEL[r.tipo] || `Retención ${String(r.tipo).toUpperCase()}`}: <strong>${fmtMonto(r.importe)}</strong>
                      {r.nro_certificado ? <span style={{ color: '#64748b', fontSize: '0.8125rem' }}> · Cert. {r.nro_certificado}</span> : null}
                    </div>
                  ))}
                  {pago.retenciones.length > 1 && (
                    <div style={{ marginTop: 2, color: '#475569', fontSize: '0.8125rem' }}>
                      Total retenciones: <strong>${fmtMonto(pago.retenciones.reduce((a, r) => a + (Number(r.importe) || 0), 0))}</strong>
                    </div>
                  )}
                  {/* Lo efectivamente pagado (transferencia/efectivo) = recibo − retenciones. */}
                  <div style={{ marginTop: 2, color: '#475569', fontSize: '0.8125rem' }}>
                    Pagado en valores: <strong>${fmtMonto(Number(pago.importe) - pago.retenciones.reduce((a, r) => a + (Number(r.importe) || 0), 0))}</strong>
                  </div>
                </dd>
              </>
            )}

            <dt style={st.dt}>Medio de pago</dt>
            <dd style={st.dd}>{pago.medio_pago?.descripcion}</dd>

            {pago.marca && (
              <>
                <dt style={st.dt}>Marca</dt>
                <dd style={st.dd}>{pago.marca}</dd>
              </>
            )}
            {pago.nro_cupon_cheque && (
              <>
                <dt style={st.dt}>Nº cupón / cheque</dt>
                <dd style={st.dd}><code>{pago.nro_cupon_cheque}</code></dd>
              </>
            )}
            {pago.cod_autorizacion && (
              <>
                <dt style={st.dt}>Cod. autorización</dt>
                <dd style={st.dd}><code>{pago.cod_autorizacion}</code></dd>
              </>
            )}
            {/* ID de transacción: el del recibo o el de cada valor. En tarjeta,
                billetera y transferencia se muestra siempre (aunque no esté cargado). */}
            {(idsTransaccion.length > 0 || ['tarjeta', 'mp', 'transferencia'].includes(pago.medio_pago?.tipo)) && (
              <>
                <dt style={st.dt}>ID transacción</dt>
                <dd style={st.dd}>
                  {idsTransaccion.length > 0
                    ? idsTransaccion.map(id => <div key={id}><code>{id}</code></div>)
                    : <span style={{ color: '#94a3b8' }}>Sin cargar</span>}
                </dd>
              </>
            )}
            {pago.cuotas > 1 && (
              <>
                <dt style={st.dt}>Cuotas</dt>
                <dd style={st.dd}>{pago.cuotas}</dd>
              </>
            )}
            {(pago.valores || []).some(v => v.fecha_pago) && (
              <>
                <dt style={st.dt}>Fecha por medio</dt>
                <dd style={st.dd}>
                  {(pago.valores || []).filter(v => v.fecha_pago).map((v, i) => (
                    <div key={i}>{({ cheque: 'Cheque', transferencia: 'Transferencia', tarjeta: 'Cupón', mp: 'Operación' })[v.tipo_valor] || v.tipo_valor}{v.nro_cupon_cheque ? ` ${v.nro_cupon_cheque}` : ''}: <strong>{fmtFecha(v.fecha_pago)}</strong></div>
                  ))}
                </dd>
              </>
            )}
            {pago.lote && (
              <>
                <dt style={st.dt}>Lote</dt>
                <dd style={st.dd}><code>{pago.lote}</code></dd>
              </>
            )}
            {pago.numero_presea && (
              <>
                <dt style={st.dt}>Nº Presea</dt>
                <dd style={st.dd}><code>{pago.numero_presea}</code></dd>
              </>
            )}
            {pago.observaciones && (
              <>
                <dt style={st.dt}>Observaciones</dt>
                <dd style={st.dd}>{pago.observaciones}</dd>
              </>
            )}
          </dl>
        </div>

        {/* Imputación a comprobantes */}
        <div style={st.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ ...st.cardTitle, margin: 0 }}>Imputación a comprobantes</h3>
            {puedeImputar && (
              <Boton variante="secondary" sm onClick={() => setShowImputar(true)}>↪ Imputar</Boton>
            )}
          </div>
          <div style={st.imputResumen}>
            <span>Aplicado <strong>${fmtMonto(imputado)}</strong></span>
            <span style={{ color: Math.abs(libre) > 0.005 ? '#1e40af' : '#16a34a' }}>
              Sin aplicar <strong>${fmtMonto(libre)}</strong>
            </span>
          </div>
          {(!pago.notas_venta || pago.notas_venta.length === 0) ? (
            <p style={st.hint}>Este recibo está libre (a cuenta): no se aplicó a ningún comprobante.</p>
          ) : (
            <div style={st.imputList}>
              {pago.notas_venta.map(nv => (
                <div key={nv.id} style={st.imputItem}>
                  <span
                    style={{ cursor: 'pointer', color: '#1e40af', textDecoration: 'underline' }}
                    onClick={() => navigate(`/notas-venta/${nv.id}`)}
                    title="Ir al detalle de la nota de venta"
                  ><strong>{nv.numero_presea || nv.numero_lgr}</strong> →</span>
                  <span style={{ fontWeight: 700 }}>${fmtMonto(nv.pivot?.monto)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Avisos internos sobre este recibo. */}
        {/* Este recibo ES un contra-recibo de devolución → link al original */}
        {esDevolucionDoc && (
          <div style={{ ...st.card, borderLeft: '3px solid #b45309' }}>
            <h3 style={st.cardTitle}>Devolución</h3>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#334155' }}>
              Este recibo es una <strong>devolución</strong> del cobro{' '}
              <span style={st.linkAzul} onClick={() => navigate(`/pagos/${pago.devolucion_de_id}`)}>
                #{pago.devolucion_de_id}{pago.devolucion_de?.numero_presea ? ` (recibo ${pago.devolucion_de.numero_presea})` : ''} ↗
              </span>
            </p>
          </div>
        )}

        {/* Devoluciones hechas sobre este cobro + neto restante */}
        {(pago.devoluciones || []).length > 0 && (
          <div style={st.card}>
            <h3 style={st.cardTitle}>Devoluciones</h3>
            {pago.devoluciones.map(d => (
              <div key={d.id} style={st.devolFila}>
                <span style={st.linkAzul} onClick={() => navigate(`/pagos/${d.id}`)}>
                  #{d.id}{d.numero_presea ? ` · ${d.numero_presea}` : ''} ↗
                </span>
                <span>{fmtFecha(d.fecha)}</span>
                <span style={{ fontWeight: 600 }}>${fmtMonto(d.importe)}</span>
                <EstadoBadgePago estado={d.estado} />
              </div>
            ))}
            <p style={{ margin: '8px 0 0', fontSize: '0.875rem', color: '#334155' }}>
              Neto cobrado: <strong>${fmtMonto(netoRestante)}</strong> de ${fmtMonto(pago.importe)}
            </p>
          </div>
        )}

        <AvisosCard tipo="pago" id={pago.id} />

        {/* Historial */}
        <div style={st.card}>
          <h3 style={st.cardTitle}>Historial de estados</h3>
          {(!pago.historial || pago.historial.length === 0) ? (
            <p style={st.hint}>Sin historial.</p>
          ) : (
            <div style={st.historialList}>
              {pago.historial.map((h, i) => (
                <div key={i} style={st.historialItem}>
                  <div style={st.historialHeader}>
                    <div style={st.historialEstados}>
                      {h.estado_anterior && (
                        <><EstadoBadgePago estado={h.estado_anterior} /><span style={st.arrow}>→</span></>
                      )}
                      <EstadoBadgePago estado={h.estado_nuevo} />
                    </div>
                    <span style={st.historialFecha}>
                      {fmtFechaHora(h.created_at)}
                    </span>
                  </div>
                  {h.usuario && (
                    <div style={st.historialUsuario}>{h.usuario.nombre}</div>
                  )}
                  {h.motivo && (
                    <div style={st.historialMotivo}>{h.motivo}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showCliente && (
        <ModalCambiarClienteRecibo
          pago={pago}
          onClose={() => setShowCliente(false)}
          onSaved={(data) => {
            setShowCliente(false)
            if (data && data.id) setPago(data)
            // Paso siguiente: imputarlo a una venta del cliente nuevo.
            setShowImputar(true)
          }}
        />
      )}
      {showImputar && (
        <ModalImputarRecibo
          pago={pago}
          onClose={() => setShowImputar(false)}
          onSaved={(data) => { setShowImputar(false); if (data && data.id) setPago(data); else cargar() }}
        />
      )}

      {showAnular && (
        <ModalAnularVerificado
          pago={pago}
          viaMobbex={viaMobbex}
          viaMp={viaMp}
          onClose={() => setShowAnular(false)}
          onDone={(data) => { setShowAnular(false); if (data && data.id) setPago(data); else cargar() }}
        />
      )}

      {showDevolucion && (
        <ModalDevolucion
          pago={pago}
          netoRestante={netoRestante}
          yaDevuelto={yaDevuelto}
          onClose={() => setShowDevolucion(false)}
          onDone={() => { setShowDevolucion(false); cargar() }}
        />
      )}
    </div>
  )
}

/**
 * Anulación DENTRO del día de un recibo emitido: guía los pasos manuales
 * (pasarela y desktop de Presea) y recién ahí deja confirmar — el backend
 * verifica los dos lados EN VIVO antes de anular; acá solo se muestra el
 * resultado.
 */
function ModalAnularVerificado({ pago, viaMobbex, viaMp, onClose, onDone }) {
  const [motivo, setMotivo] = useState('')
  const [errorModal, setErrorModal] = useState('')
  const [enviando, setEnviando] = useState(false)

  const confirmar = async () => {
    setEnviando(true); setErrorModal('')
    try {
      const { data } = await pagosApi.anular(pago.id, motivo || undefined)
      onDone(data)
    } catch (err) {
      setErrorModal(err.response?.data?.errors?.estado?.[0] || err.response?.data?.message || 'No se pudo anular.')
    } finally { setEnviando(false) }
  }

  const pasos = []
  if (viaMobbex) pasos.push('Anulá la operación en el panel de Mobbex (LGR va a consultar la API y frena si sigue aprobada).')
  if (viaMp) pasos.push('Cancelá o devolvé el pago en MercadoPago (LGR va a consultar la API y frena si sigue aprobado).')
  pasos.push(`Anulá el recibo ${pago.numero_presea} en el desktop de Presea — tiene que quedar como "ANUL.".`)
  pasos.push('Confirmá acá abajo: LGR verifica todo en vivo y recién ahí anula el cobro.')

  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={e => e.stopPropagation()}>
        <h3 style={st.modalTitle}>Anular recibo {pago.numero_presea}</h3>
        <p style={st.modalSub}>
          Anulación <strong>del día</strong> ({fmtFecha(pago.fecha)}) por ${fmtMonto(pago.importe)}. Nada se anula por palabra:
        </p>
        <ol style={st.pasos}>
          {pasos.map((p, i) => <li key={i} style={{ marginBottom: 6 }}>{p}</li>)}
        </ol>
        <label style={st.modalLabel}>Motivo (queda en el historial)</label>
        <textarea style={st.textarea} rows={2} value={motivo} onChange={e => setMotivo(e.target.value)}
          placeholder="Por qué se anula este cobro…" />
        {errorModal && <div style={st.errorBox}>{errorModal}</div>}
        <div style={st.modalBotones}>
          <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
          <Boton variante="destructive" disabled={enviando} onClick={confirmar}>
            {enviando ? 'Verificando…' : '✓ Verificar y anular'}
          </Boton>
        </div>
      </div>
    </div>
  )
}

/**
 * Devolución con contra-recibo NEGATIVO (total o parcial): LGR lo emite en
 * Presea por el WS — el desktop no interviene. El tilde de aviso existe por el
 * caso de la corrección administrativa: si la plata NO volvió, el mail
 * "te devolvimos" es mentira y no debe salir.
 */
function ModalDevolucion({ pago, netoRestante, yaDevuelto, onClose, onDone }) {
  const [monto, setMonto] = useState(String(netoRestante))
  const [motivo, setMotivo] = useState('')
  const [avisar, setAvisar] = useState(true)
  const [errorModal, setErrorModal] = useState('')
  const [enviando, setEnviando] = useState(false)

  const montoNum = parseFloat(monto) || 0
  const esTotal = Math.abs(montoNum - netoRestante) < 0.01

  const confirmar = async () => {
    setEnviando(true); setErrorModal('')
    try {
      await pagosApi.devolucion(pago.id, { monto: montoNum, motivo, avisar_cliente: avisar })
      onDone()
    } catch (err) {
      const errs = err.response?.data?.errors
      setErrorModal(errs?.pago?.[0] || errs?.monto?.[0] || errs?.motivo?.[0] || err.response?.data?.message || 'No se pudo registrar la devolución.')
    } finally { setEnviando(false) }
  }

  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={e => e.stopPropagation()}>
        <h3 style={st.modalTitle}>Devolución del recibo {pago.numero_presea}</h3>
        <p style={st.modalSub}>
          Cobro de <strong>${fmtMonto(pago.importe)}</strong>
          {yaDevuelto > 0.005 && <> · ya devuelto ${fmtMonto(yaDevuelto)}</>}
          {' '}· queda por devolver <strong>${fmtMonto(netoRestante)}</strong>.
          LGR emite el <strong>contra-recibo negativo</strong> en Presea (el desktop no interviene).
          Si la plata vuelve por Mobbex/MP/banco, el reintegro allá se hace aparte — esto registra y emite el comprobante.
        </p>
        <label style={st.modalLabel}>Monto a devolver</label>
        <MoneyInput style={st.inputMonto} value={monto} onChange={setMonto} />
        <div style={{ fontSize: '0.8125rem', color: '#64748b', marginTop: 2 }}>
          {esTotal ? 'Devolución TOTAL: el cobro queda con neto $0.' : 'Devolución PARCIAL: el cobro sigue vigente por el resto.'}
        </div>
        <label style={st.modalLabel}>Motivo (obligatorio — queda en el historial)</label>
        <textarea style={st.textarea} rows={2} value={motivo} onChange={e => setMotivo(e.target.value)}
          placeholder="Por qué se devuelve…" />
        <label style={st.tilde}>
          <input type="checkbox" checked={avisar} onChange={e => setAvisar(e.target.checked)} />
          <span>
            Avisar al cliente por mail (&quot;te devolvimos ${fmtMonto(montoNum || netoRestante)}&quot;).{' '}
            <strong>Destildalo si es una corrección administrativa</strong> y la plata no volvió — ese mail sería mentira.
          </span>
        </label>
        {errorModal && <div style={st.errorBox}>{errorModal}</div>}
        <div style={st.modalBotones}>
          <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
          <Boton variante="destructive" disabled={enviando || !motivo.trim() || montoNum <= 0} onClick={confirmar}>
            {enviando ? 'Emitiendo…' : 'Emitir devolución'}
          </Boton>
        </div>
      </div>
    </div>
  )
}

const st = {
  wrap: { maxWidth: '900px' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
  modal: { background: '#fff', borderRadius: 10, padding: '1.25rem 1.5rem', width: 'min(560px, 100%)', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.18)' },
  modalTitle: { margin: '0 0 4px', fontSize: '1.05rem', fontWeight: 600, color: '#0f172a' },
  modalSub: { margin: '0 0 10px', fontSize: '0.875rem', color: '#334155', lineHeight: 1.5 },
  modalLabel: { display: 'block', margin: '10px 0 4px', fontSize: '0.8125rem', fontWeight: 600, color: '#334155' },
  pasos: { margin: '0 0 6px', paddingLeft: '1.25rem', fontSize: '0.875rem', color: '#334155', lineHeight: 1.5 },
  textarea: { width: '100%', boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: 6, padding: '8px 10px', fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical' },
  inputMonto: { width: 180, border: '1px solid #cbd5e1', borderRadius: 6, padding: '8px 10px', fontSize: '0.9375rem', fontVariantNumeric: 'tabular-nums' },
  tilde: { display: 'flex', gap: 8, alignItems: 'flex-start', margin: '12px 0 0', fontSize: '0.8125rem', color: '#334155', lineHeight: 1.5 },
  modalBotones: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 },
  linkAzul: { color: '#1e40af', cursor: 'pointer', fontWeight: 500 },
  devolFila: { display: 'flex', gap: 12, alignItems: 'center', fontSize: '0.875rem', color: '#334155', padding: '4px 0' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem', gap: '1rem', flexWrap: 'wrap' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  headerActions: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  title: { margin: 0, fontSize: '1.25rem', color: '#1e293b' },
  errorBox: { background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  card: { background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  cardTitle: { margin: '0 0 1rem', fontSize: '0.9375rem', fontWeight: '700', color: '#1e293b' },
  hint: { color: '#94a3b8', fontSize: '0.875rem' },
  dl: { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.375rem 1rem', alignItems: 'baseline' },
  dt: { fontWeight: '600', fontSize: '0.8125rem', color: '#64748b', whiteSpace: 'nowrap' },
  dd: { margin: 0, fontSize: '0.9rem', color: '#1e293b' },
  historialList: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  historialItem: { background: '#f8fafc', borderRadius: '6px', padding: '0.625rem 0.75rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  historialHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  historialEstados: { display: 'flex', alignItems: 'center', gap: '0.375rem' },
  arrow: { color: '#94a3b8', fontSize: '0.75rem' },
  historialFecha: { color: '#64748b', fontSize: '0.75rem', whiteSpace: 'nowrap' },
  historialUsuario: { color: '#475569', fontSize: '0.8125rem' },
  historialMotivo: { color: '#64748b', fontSize: '0.8125rem', fontStyle: 'italic' },
  imputResumen: { display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.5rem 0.75rem', background: '#f8fafc', borderRadius: '6px', marginBottom: '0.75rem', fontSize: '0.8125rem', color: '#475569' },
  imputList: { display: 'flex', flexDirection: 'column', gap: '0.375rem' },
  imputItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.625rem', background: '#f8fafc', borderRadius: '6px', fontSize: '0.875rem' },
}
